import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalBody,
  ModalFooter,
  Button,
  Flex,
  Text,
  Checkbox,
  useModalContext,
  Skeleton,
  Stack,
  Box,
} from '@chakra-ui/react';
import {TestList} from '../../../shared/testLists';
import {WithId} from '../../../shared/util';
import {useGetTestLists} from '../../../hooks/api/testList';
import {ObjectId} from 'bson';
import {useSelectTestListStore} from './SelectTestListsModal.state';
import {TestListLabel} from './TestListLabel';

export const SelectTestListsModal = ({
  isOpen,
  onClose,
  onSelectTestLists,
  selectButtonLoading,
  selectButtonText,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelectTestLists: (testLists: ObjectId[]) => void;
  selectButtonLoading?: boolean;
  selectButtonText?: string;
}) => {
  return (
    <Modal isCentered={true} isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Select Test Lists</ModalHeader>

        <SelectTestListModalContent
          selectButtonText={selectButtonText}
          selectButtonLoading={selectButtonLoading}
          onSelectTestLists={onSelectTestLists}
        />
      </ModalContent>
    </Modal>
  );
};

const SelectTestListModalContent = ({
  onSelectTestLists,
  selectButtonLoading,
  selectButtonText,
}: {
  onSelectTestLists: (testLists: ObjectId[]) => void;
  selectButtonLoading?: boolean;
  selectButtonText?: string;
}) => {
  const {data, isPending} = useGetTestLists({});
  const {testLists} = useSelectTestListStore();
  const {onClose} = useModalContext();

  return (
    <>
      <ModalBody>
        {isPending ? (
          <Stack gap="4">
            <Skeleton height="40px" />
            <Skeleton height="40px" />
          </Stack>
        ) : (
          <TestListTable testLists={data ?? []} />
        )}
      </ModalBody>
      <ModalFooter as={Flex} justifyContent="space-between">
        <Button onClick={onClose} variant="ghost">
          Cancel
        </Button>
        <Button
          colorScheme="kbuttonblue"
          isLoading={selectButtonLoading}
          isDisabled={isPending || !testLists.length}
          onClick={() => {
            onSelectTestLists(testLists);
            onClose();
          }}
        >
          {selectButtonText ?? 'Select'}
        </Button>
      </ModalFooter>
    </>
  );
};

const TestListRow = ({testList}: {testList: WithId<TestList>}) => {
  const {testLists, selectTestList, deselectTestList} =
    useSelectTestListStore();
  const isSelected = testLists.some((id) => id.equals(testList._id));

  return (
    <Flex
      alignItems="center"
      border="1px solid"
      borderColor="kgray.200"
      borderRadius="md"
      cursor="pointer"
      justifyContent="space-between"
      onClick={(e) => {
        // Prevent the checkbox from receiving the click
        e.preventDefault();
        isSelected
          ? deselectTestList(testList._id)
          : selectTestList(testList._id);
      }}
      p="4"
      _hover={{
        bg: 'kgray.100',
      }}
    >
      <Flex gap="4">
        <Checkbox isChecked={isSelected} size="lg" />
        <TestListLabel testList={testList} />
      </Flex>
      <Text color="kgray.300">
        {testList.status.type === 'processing'
          ? 'Processing'
          : `${testList.status.numAccounts.toLocaleString()} accounts`}
      </Text>
    </Flex>
  );
};

const TestListTable = ({testLists}: {testLists: WithId<TestList>[]}) => {
  if (!testLists.length) {
    return <Box>No test lists available. Please import a test list first.</Box>;
  }

  return (
    <Flex direction="column" gap="2">
      {testLists.map((testList) => (
        <TestListRow key={testList._id.toString()} testList={testList} />
      ))}
    </Flex>
  );
};
